// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputFormProps,
	InputTextWithoutInputTextBoxProps,
} from '@app/props';

interface UseInputPolicyNumberParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	value: string;
	showValidation?: boolean;
}

type UseInputPolicyNumberResults = UseInputFormResult<number, InputTextWithoutInputTextBoxProps>

const useInputPolicyNumber = (params: UseInputPolicyNumberParameters): UseInputPolicyNumberResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyPolicyNumberInputProps = (): InputTextWithoutInputTextBoxProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the dropdown props.
		return {
			...formProps,
			infoText: 'The policy number starts with NCIP or CNCIP',
			onChange: setInput,
			value: value,
		}
	};

	// #endregion exported methods


	/**
	 * Validates the current value.
	 * @param currentValue
	 */
	const validatePolicyNumber = (currentValue: string, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& String.isNullOrWhiteSpace(currentValue)) {
			// Make sure there is a policy number value, if required.
			response = new ValidatorResponse(false, 'Policy number is required.');
		}
		else if (!String.isNullOrWhiteSpace(currentValue)
			&& !(new RegExp(/^(NCIP|CNCIP)?\d{6,}(-\d{4})?$/i).test(currentValue))) {
			// Make sure the policy number is valid, if a value is provided.
			response = new ValidatorResponse(false, 'Please provide a valid policy number that is six digits or longer.');
		}

		return response;
	}

	const getPolicyNumber = (): number => {
		let policyNumber: number = 0;
		let matches: RegExpMatchArray | null = value.match(/\d{6,}/i);
		if (matches !== null && matches.length > 0) {
			policyNumber = isNaN(parseInt(matches[0])) ? 0 : parseInt(matches[0]);
		}
		return policyNumber;
	}

	const setPolicyNumber = (policyNumber: number): void => {
		setInput(policyNumber.toString());
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'address' input.
	const { applyProps, value, setInput, ...other } = useInputForm<string>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.value,
		showValidation: params.showValidation,
		validators: [validatePolicyNumber],
	});

	// #endregion variables


	return {
		applyProps: applyPolicyNumberInputProps,
		value: getPolicyNumber(),
		setInput: setPolicyNumber,
		...other,
	};
}

// Export parameters.
export {
	UseInputPolicyNumberParameters,
}

// Export results.
export {
	UseInputPolicyNumberResults,
}

export default useInputPolicyNumber;